/**
 * DecisionFlow — DataSetupFlowShell
 * data-fit LLC · Confidential
 * Stage 3: DATA SETUP — Screen SETUP-00
 * Spec authority: ComponentSpec_DATASETUP_v1_corrected.md (Session 90)
 * MasterContext authority: MasterContext_Session90_Close.md
 * Generated: Session 90 — ChatGPT Phase 3
 * Patched: Session 91 — PATCH-01 (token/user removed from sharedChildProps — ARCH-2)
 * Patched: S205 — CSS fix: df-* classes replaced with Tailwind utilities (no new CSS files)
 * Path: ui/src/components/tenant-admin/DataSetupFlowShell.jsx
 * M040 C2: hardcoded colors → tokens, text-* → text-tok-*
 *
 * OPEN ITEMS IN THIS FILE:
 * None
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../auth/AuthContext.jsx';

import PositionDataReview from './PositionDataReview';
import OteHistoryDataReview from './OteHistoryDataReview';
import QuotaDataReview from './QuotaDataReview';
import CreditDataReview from './CreditDataReview';
import RampScheduleConfig from './RampScheduleConfig';
import PlanBuilder from './PlanBuilder';
import RateTableBuilder from './RateTableBuilder';
import PlanAssignmentManager from './PlanAssignmentManager';
import DataReadinessSummary from './DataReadinessSummary';

export const API_BASE = '/api/v1';
export const AI_TIMEOUT_MS = 10000;

export const SCREEN_ORDER = [
  'positions',
  'ote_history',
  'quotas',
  'ramp',
  'plans',
  'rates',
  'assignments',
  'credits',
  'readiness'
];

export function timeoutPromise(ms) {
  return new Promise((_, reject) => {
    window.setTimeout(() => {
      reject(new Error('AI_TIMEOUT'));
    }, ms);
  });
}

const SCREEN_LABELS = {
  positions:   'Positions',
  ote_history: 'OTE History',
  quotas:      'Quotas',
  ramp:        'Ramp Schedules',
  plans:       'Plans',
  rates:       'Rate Tables',
  assignments: 'Assignments',
  credits:     'Credits',
  readiness:   'Readiness'
};

function normalizeInitialScreen(initialScreen) {
  return SCREEN_ORDER.includes(initialScreen) ? initialScreen : SCREEN_ORDER[0];
}

function DataSetupFlowShell({
  tenantId = null,
  tier = 'essentials',
  initialScreen = SCREEN_ORDER[0]
}) {
  const { token, user } = useAuth();

  const [currentScreen, setCurrentScreen] = useState(() => normalizeInitialScreen(initialScreen));
  const [aiStatus, setAiStatus] = useState('idle');
  const [aiResponse, setAiResponse] = useState([]);
  const [toast, setToast] = useState(null);
  const [blockingError, setBlockingError] = useState('');

  const currentIndex = useMemo(() => SCREEN_ORDER.indexOf(currentScreen), [currentScreen]);

  const showToast = useCallback((message) => {
    setToast(message);
    window.setTimeout(() => { setToast(null); }, 3000);
  }, []);

  const dispatchDecisionFlowNavigation = useCallback((detail) => {
    window.dispatchEvent(new CustomEvent('decisionflow:navigate', { detail }));
  }, []);

  const navigateToConfigure = useCallback(() => {
    dispatchDecisionFlowNavigation({ target: 'CONFIGURE', source: 'SETUP' });
  }, [dispatchDecisionFlowNavigation]);

  const navigateToExecute = useCallback(() => {
    dispatchDecisionFlowNavigation({ target: 'EXECUTE', source: 'SETUP' });
  }, [dispatchDecisionFlowNavigation]);

  const goToScreen = useCallback((screen) => {
    if (!SCREEN_ORDER.includes(screen)) {
      setBlockingError(`Unknown DATA SETUP screen: ${screen}`);
      return;
    }
    setBlockingError('');
    setCurrentScreen(screen);
  }, []);

  const goBack = useCallback(() => {
    setBlockingError('');
    setCurrentScreen((previousScreen) => {
      const previousIndex = SCREEN_ORDER.indexOf(previousScreen);
      if (previousIndex <= 0) { navigateToConfigure(); return previousScreen; }
      return SCREEN_ORDER[previousIndex - 1];
    });
  }, [navigateToConfigure]);

  const goNext = useCallback(() => {
    setBlockingError('');
    setCurrentScreen((previousScreen) => {
      const previousIndex = SCREEN_ORDER.indexOf(previousScreen);
      if (previousIndex === -1) { setBlockingError(`Unknown DATA SETUP screen: ${previousScreen}`); return previousScreen; }
      if (previousIndex >= SCREEN_ORDER.length - 1) { navigateToExecute(); return previousScreen; }
      return SCREEN_ORDER[previousIndex + 1];
    });
  }, [navigateToExecute]);

  const handleExternalNavigation = useCallback((event) => {
    const detail = event?.detail || {};
    if (detail.target !== 'SETUP') return;
    const requestedScreen = detail.screen || SCREEN_ORDER[0];
    if (!SCREEN_ORDER.includes(requestedScreen)) {
      setBlockingError(`Unknown DATA SETUP screen: ${requestedScreen}`);
      showToast('Unable to navigate to requested DATA SETUP screen.');
      return;
    }
    setBlockingError('');
    setCurrentScreen(requestedScreen);
  }, [showToast]);

  useEffect(() => {
    window.addEventListener('decisionflow:navigate', handleExternalNavigation);
    return () => { window.removeEventListener('decisionflow:navigate', handleExternalNavigation); };
  }, [handleExternalNavigation]);

  useEffect(() => {
    const normalized = normalizeInitialScreen(initialScreen);
    setCurrentScreen(normalized);
    setBlockingError('');
  }, [initialScreen]);

  // ARCH-2: token and user are NOT passed as props. Children call useAuth() directly.
  const sharedChildProps = useMemo(
    () => ({ tier, tenantId, onBack: goBack, onNext: goNext }),
    [tier, tenantId, goBack, goNext]
  );

  const renderedScreen = useMemo(() => {
    switch (currentScreen) {
      case 'positions':    return <PositionDataReview {...sharedChildProps} />;
      case 'ote_history':  return <OteHistoryDataReview {...sharedChildProps} />;
      case 'quotas':       return <QuotaDataReview {...sharedChildProps} />;
      case 'ramp':         return <RampScheduleConfig {...sharedChildProps} />;
      case 'plans':        return <PlanBuilder {...sharedChildProps} />;
      case 'rates':        return <RateTableBuilder {...sharedChildProps} />;
      case 'assignments':  return <PlanAssignmentManager {...sharedChildProps} />;
      case 'credits':      return <CreditDataReview {...sharedChildProps} />;
      case 'readiness':
        return (
          <DataReadinessSummary
            {...sharedChildProps}
            aiStatus={aiStatus}
            aiResponse={aiResponse}
            onAiStatusChange={setAiStatus}
            onAiResponseChange={setAiResponse}
            onComplete={navigateToExecute}
          />
        );
      default: return null;
    }
  }, [aiResponse, aiStatus, currentScreen, navigateToExecute, sharedChildProps]);

  if (!user) {
    return (
      <section className="flex flex-col gap-4 p-6 min-h-full bg-[rgb(var(--color-bg))]">
        <h1 className="text-tok-2xl font-semibold text-[rgb(var(--color-text))]">Data Setup</h1>
        <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
          Authentication is required to continue DATA SETUP.
        </div>
      </section>
    );
  }

  if (blockingError) {
    return (
      <section className="flex flex-col gap-4 p-6 min-h-full bg-[rgb(var(--color-bg))]">
        <h1 className="text-tok-2xl font-semibold text-[rgb(var(--color-text))]">Data Setup</h1>
        <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
          {blockingError}
        </div>
        <div className="flex gap-2">
          <button type="button" className="rounded-lg bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] px-4 py-2 text-tok-sm font-medium text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))]" onClick={() => goToScreen(SCREEN_ORDER[0])}>
            Return to first setup screen
          </button>
          <button type="button" className="rounded-lg bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] px-4 py-2 text-tok-sm font-medium text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))]" onClick={navigateToConfigure}>
            Back to Configure
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="flex flex-col gap-6 p-6 min-h-full bg-[rgb(var(--color-bg))]">
      {/* Header */}
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-tok-xs font-semibold uppercase tracking-widest text-[rgb(var(--color-text-muted))] mb-1">Stage 3</p>
          <h1 className="text-tok-2xl font-semibold text-[rgb(var(--color-text))]">Data Setup</h1>
          <p className="mt-1 text-tok-sm text-[rgb(var(--color-text-muted))]">
            Review imported data, configure plans, assign plans, and confirm readiness before execution.
          </p>
        </div>
        <div className="flex items-center gap-3 text-tok-xs text-[rgb(var(--color-text-muted))]">
          <span className="rounded-full bg-[rgb(var(--color-surface-muted))] px-2 py-1">Tier: {tier}</span>
          {tenantId ? <span className="rounded-full bg-[rgb(var(--color-surface-muted))] px-2 py-1">Tenant context active</span> : null}
        </div>
      </header>

      {/* Step nav */}
      <nav className="flex items-center gap-1 flex-wrap" aria-label="Data setup screens">
        {SCREEN_ORDER.map((screen, index) => {
          const isActive = screen === currentScreen;
          const isComplete = index < currentIndex;
          return (
            <button
              key={screen}
              type="button"
              className={[
                'flex items-center gap-2 rounded-lg px-3 py-2 text-tok-sm font-medium transition-colors',
                isActive
                  ? 'bg-[rgb(var(--color-primary))] text-white shadow-sm'
                  : isComplete
                  ? 'bg-[rgb(var(--color-success))]/10 text-[rgb(var(--color-success))] hover:bg-[rgb(var(--color-success))]/20'
                  : 'bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))]'
              ].join(' ')}
              aria-current={isActive ? 'step' : undefined}
              onClick={() => goToScreen(screen)}
            >
              <span className={[
                'flex h-5 w-5 items-center justify-center rounded-full text-tok-xs font-semibold',
                isActive ? 'bg-white/20 text-white' : isComplete ? 'bg-[rgb(var(--color-success))]/20 text-[rgb(var(--color-success))]' : 'bg-[rgb(var(--color-surface-muted))] text-[rgb(var(--color-text-muted))]'
              ].join(' ')}>
                {index + 1}
              </span>
              <span>{SCREEN_LABELS[screen]}</span>
            </button>
          );
        })}
      </nav>

      {/* Toast */}
      {toast ? (
        <div role="status" className="fixed bottom-4 right-4 z-50 rounded-lg bg-[rgb(var(--color-primary))] px-4 py-3 text-tok-sm text-white shadow-lg">
          {toast}
        </div>
      ) : null}

      {/* Screen content */}
      <main className="flex-1">
        {renderedScreen}
      </main>
    </section>
  );
}

export default DataSetupFlowShell;
